/**
 * JobClose AI · Slice 2T · Levi 1.0 Intelligence
 * ---------------------------------------------------
 * deriveLeviAvailableActions(facts, recommendations, ctx) → LeviActions
 *
 * Pure function. Maps recommendation `action_id`s to concrete existing
 * routes, in-page handlers, or "open the existing Levi drawer".
 *
 * NEVER emits mutation actions. NEVER invents routes. Every route is
 * proven to exist in the app.
 */

/**
 * Whitelist of action_ids that ARE allowed to appear in the output.
 * The recommendation layer may reference any action id; if it is not
 * in this whitelist, the mapping is skipped (defensive).
 */
const ALLOWED_ACTION_IDS = new Set([
  "open_estimate",
  "create_estimate",
  "edit_intake_notes",
  "jump_missing_pricing",
  "run_double_check",
  "open_first_shared_proposal",
  "open_first_pending_contract",
  "open_first_unpaid_invoice",
  "new_proposal",
  "new_contract",
  "new_invoice",
  "generate_closeout",
  "ask_levi",
]);

/**
 * Explicit forbidden actions. Any candidate that tries to emit these is
 * dropped. Prevents accidental introduction of mutations later.
 */
const FORBIDDEN_ACTION_IDS = new Set([
  "approve_estimate",
  "share_proposal",
  "share_contract",
  "revoke_share",
  "sign_contract",
  "mark_invoice_paid",
  "send_email",
  "send_sms",
  "assign_crew",
  "schedule_job",
  "mutate_pricing",
]);

const _findFirstPendingContract = (contracts) =>
  (contracts || []).find(
    (c) => c && c.status && c.status !== "signed" && c.status !== "fully_executed" && c.status !== "canceled",
  ) || null;

const _findFirstSharedProposal = (proposals) =>
  (proposals || []).find(
    (p) => p && p.sharing && (p.sharing.state === "shared" || p.sharing.state === "approved"),
  ) || null;

const _findFirstUnpaidInvoice = (invoices) =>
  (invoices || []).find(
    (i) => i && i.payment_status && i.payment_status !== "paid",
  ) || null;

/**
 * @param {object} facts               - deriveLeviFacts output
 * @param {object} recommendations     - deriveLeviRecommendations output
 * @param {object} ctx
 * @param {string} [ctx.projectId]     - project id for workspace context
 * @param {Array}  [ctx.proposals]     - source list for concrete id lookups
 * @param {Array}  [ctx.contracts]
 * @param {Array}  [ctx.invoices]
 * @returns {{by_id: object, ask_levi_action: object}}
 */
export function deriveLeviAvailableActions(facts, recommendations, ctx) {
  const f = facts || {};
  const context = ctx || {};
  const primaryEstimateId = f.primary_estimate_id || null;
  const projectId = context.projectId || null;

  const byId = {};

  const put = (id, spec) => {
    if (!ALLOWED_ACTION_IDS.has(id)) return;
    if (FORBIDDEN_ACTION_IDS.has(id)) return;
    byId[id] = { ...spec, id };
  };

  // open_estimate → /app/estimates/{primary_estimate_id}
  if (primaryEstimateId) {
    put("open_estimate", {
      label_key: "levi_project_snapshot.action.open_estimate",
      kind: "navigate",
      to: `/app/estimates/${primaryEstimateId}`,
      testid: "levi-cta-open-estimate",
    });
    put("jump_missing_pricing", {
      label_key: "levi_project_snapshot.action.jump_missing_pricing",
      kind: "navigate",
      to: `/app/estimates/${primaryEstimateId}`,
      testid: "levi-cta-jump-missing-pricing",
    });
    put("run_double_check", {
      label_key: "levi_project_snapshot.action.run_double_check",
      kind: "navigate",
      to: `/app/estimates/${primaryEstimateId}`,
      testid: "levi-cta-run-double-check",
    });
  }

  // create_estimate → /app/estimates/new?project=<pid>
  if (projectId) {
    put("create_estimate", {
      label_key: "levi_project_snapshot.action.create_estimate",
      kind: "navigate",
      to: `/app/estimates/new?project=${encodeURIComponent(projectId)}`,
      testid: "levi-cta-create-estimate",
    });
  }

  // edit_intake_notes → in-page handler owned by host.
  put("edit_intake_notes", {
    label_key: "levi_project_snapshot.action.edit_intake_notes",
    kind: "in_page_handler",
    handler_id: "open_intake_edit_panel",
    testid: "levi-cta-edit-intake-notes",
  });

  // Proposal / contract / invoice list-level "new" (routes proven in App.js).
  put("new_proposal", {
    label_key: "levi_project_snapshot.action.new_proposal",
    kind: "navigate",
    to: "/app/proposals",
    testid: "levi-cta-new-proposal",
  });
  put("new_contract", {
    label_key: "levi_project_snapshot.action.new_contract",
    kind: "navigate",
    to: "/app/contracts",
    testid: "levi-cta-new-contract",
  });
  put("new_invoice", {
    label_key: "levi_project_snapshot.action.new_invoice",
    kind: "navigate",
    to: "/app/invoices",
    testid: "levi-cta-new-invoice",
  });

  // Concrete "first pending" navigations.
  const shared = _findFirstSharedProposal(context.proposals);
  if (shared && shared.id) {
    put("open_first_shared_proposal", {
      label_key: "levi_project_snapshot.action.open_shared_proposal",
      kind: "navigate",
      to: `/app/proposals/${shared.id}`,
      testid: "levi-cta-open-shared-proposal",
    });
  }
  const pendingContract = _findFirstPendingContract(context.contracts);
  if (pendingContract && pendingContract.id) {
    put("open_first_pending_contract", {
      label_key: "levi_project_snapshot.action.open_pending_contract",
      kind: "navigate",
      to: `/app/contracts/${pendingContract.id}`,
      testid: "levi-cta-open-pending-contract",
    });
  }
  const unpaid = _findFirstUnpaidInvoice(context.invoices);
  if (unpaid && unpaid.id) {
    put("open_first_unpaid_invoice", {
      label_key: "levi_project_snapshot.action.open_unpaid_invoice",
      kind: "navigate",
      to: `/app/invoices/${unpaid.id}`,
      testid: "levi-cta-open-unpaid-invoice",
    });
  }

  // generate_closeout → in-page handler on ProjectDetail.
  put("generate_closeout", {
    label_key: "levi_project_snapshot.action.generate_closeout",
    kind: "in_page_handler",
    handler_id: "generate_closeout_packet",
    testid: "levi-cta-generate-closeout",
  });

  const askLeviAction = {
    id: "ask_levi",
    label_key: "levi_project_snapshot.ask_btn",
    kind: "opens",
    opens: "levi_drawer",
    testid: "levi-project-snapshot-ask-btn",
  };

  return {
    by_id: byId,
    ask_levi_action: askLeviAction,
  };
}

export default deriveLeviAvailableActions;
